"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTour } from "@/context/TourContext";
import React, { useState } from "react";
import { LuCircle, LuPlay, LuRotateCcw } from "react-icons/lu";

interface TourTriggerProps {
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "default" | "lg";
  className?: string;
  showDialog?: boolean;
}

export const TourTrigger: React.FC<TourTriggerProps> = ({
  variant = "outline",
  size = "sm",
  className = "",
  showDialog = true,
}) => {
  const { tourState, startTour, resetTour } = useTour();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleStartTour = (tourType: "full" | "drawer" = "full") => {
    // Start tour by ID instead of passing steps directly
    startTour(tourType === "drawer" ? "drawer-tour" : "full-tour");
    setIsDialogOpen(false);
  };

  const handleResetTour = () => {
    resetTour();
    localStorage.removeItem("tour-completed");
    setIsDialogOpen(false);
  };

  const isTourCompleted = localStorage.getItem("tour-completed") === "true";

  if (showDialog) {
    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={`gap-2 ${className}`}
          >
            <LuCircle className="h-4 w-4" />
            {isTourCompleted ? "Tour de Nuevo" : "Tomar Tour"}
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LuPlay className="h-5 w-5 text-blue-500" />
              Bienvenido a Orquesti
            </DialogTitle>
            <DialogDescription className="text-left">
              {isTourCompleted ? (
                <>
                  ¡Ya has completado el tour! Elige qué tour te gustaría tomar
                  de nuevo:
                  <br />
                  <br />
                  <strong>Tour Completo:</strong> Resumen completo de todas las
                  funciones
                  <br />
                  <strong>Tour del Panel:</strong> Enfocado en la estructura de
                  navegación y organización del panel
                </>
              ) : (
                <>
                  ¡Bienvenido! Elige el tour que mejor se adapte a tus
                  necesidades:
                  <br />
                  <br />
                  <strong>Tour Completo:</strong> Resumen completo de todas las
                  funciones (~16 pasos)
                  <br />
                  <strong>Tour del Panel:</strong> Enfocado en la estructura de
                  navegación (~14 pasos)
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => handleStartTour("full")}
              className="w-full gap-2"
              disabled={tourState.isActive}
            >
              <LuPlay className="h-4 w-4" />
              {isTourCompleted
                ? "Iniciar Tour Completo de Nuevo"
                : "Iniciar Tour Completo"}
            </Button>

            <Button
              onClick={() => handleStartTour("drawer")}
              variant="outline"
              className="w-full gap-2"
              disabled={tourState.isActive}
            >
              <LuPlay className="h-4 w-4" />
              {isTourCompleted
                ? "Iniciar Tour del Panel de Nuevo"
                : "Iniciar Tour del Panel"}
            </Button>

            {isTourCompleted && (
              <Button
                onClick={handleResetTour}
                variant="outline"
                className="w-full gap-2"
              >
                <LuRotateCcw className="h-4 w-4" />
                Restablecer Estado del Tour
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => handleStartTour("full")}
      className={`gap-2 ${className}`}
      disabled={tourState.isActive}
    >
      <LuPlay className="h-4 w-4" />
      {isTourCompleted ? "Tour Again" : "Take Tour"}
    </Button>
  );
};
